import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  RefreshCw,
  Server,
  Database,
  Link,
  Globe,
  Key
} from 'lucide-react';

interface ConfigData {
  summary: {
    status: 'healthy' | 'warning' | 'error';
    message: string;
  };
  environment: {
    NODE_ENV: string;
    hostname: string;
    origin: string;
    isProduction: boolean;
    isPreview: boolean;
    isLocal: boolean;
    variables?: Record<string, any>;
  };
  bindings: {
    database: {
      configured: boolean;
      type: string;
    };
    services: {
      [key: string]: {
        configured: boolean;
        binding: string;
        expectedService: string;
        reachable?: boolean;
      };
    };
  };
  warnings: string[];
  errors: string[];
  timestamp: string;
}

export const ConfigDashboard: React.FC = () => {
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [token, setToken] = useState('debug-config-2024');

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const isLocal = window.location.hostname === 'localhost';
      const url = isLocal ? '/api/config' : `/api/config?token=${token}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.statusText}`);
      }
      
      const data = await response.json();
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getEnvironmentBadge = (env: ConfigData['environment']) => {
    if (env.isProduction) return <Badge className="bg-red-100 text-red-800">Production</Badge>;
    if (env.isPreview) return <Badge className="bg-yellow-100 text-yellow-800">Preview</Badge>;
    if (env.isLocal) return <Badge className="bg-green-100 text-green-800">Local</Badge>;
    return <Badge>Unknown</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-red-800">
            <XCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          {!window.location.hostname.includes('localhost') && (
            <div className="mt-4">
              <input
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter debug token"
                className="px-3 py-2 border rounded mr-2"
              />
              <Button onClick={() => setShowToken(!showToken)} size="sm" variant="outline">
                {showToken ? 'Hide' : 'Show'}
              </Button>
              <Button onClick={fetchConfig} size="sm" className="ml-2">
                Retry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Configuration Dashboard</h2>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            Last updated: {new Date(config.timestamp).toLocaleTimeString()}
          </span>
          <Button onClick={fetchConfig} size="sm" variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Summary */}
      <Card className={
        config.summary.status === 'error' ? 'border-red-200' :
        config.summary.status === 'warning' ? 'border-yellow-200' :
        'border-green-200'
      }>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getStatusIcon(config.summary.status)}
              <div>
                <p className="font-semibold">{config.summary.message}</p>
                <p className="text-sm text-gray-600">System Status</p>
              </div>
            </div>
            {getEnvironmentBadge(config.environment)}
          </div>
        </CardContent>
      </Card>

      {/* Errors and Warnings */}
      {(config.errors.length > 0 || config.warnings.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {config.errors.length > 0 && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-800 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Errors ({config.errors.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {config.errors.map((error, idx) => (
                    <li key={idx} className="text-sm text-red-700">
                      • {error}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          
          {config.warnings.length > 0 && (
            <Card className="border-yellow-200">
              <CardHeader>
                <CardTitle className="text-yellow-800 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Warnings ({config.warnings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {config.warnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-yellow-700">
                      • {warning}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Environment Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Environment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">NODE_ENV</p>
              <p className="font-mono">{config.environment.NODE_ENV}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Hostname</p>
              <p className="font-mono text-sm">{config.environment.hostname}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Origin</p>
              <p className="font-mono text-sm">{config.environment.origin}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Bindings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="w-5 h-5" />
            Service Bindings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Database */}
            <div className="p-3 bg-gray-50 rounded">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Database className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium">{config.bindings.database.name || 'Database'}</p>
                    <p className="text-sm text-gray-600">Binding: {config.bindings.database.binding}</p>
                  </div>
                </div>
                {config.bindings.database.configured ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Type: <span className="font-medium">{config.bindings.database.type}</span></div>
                {config.bindings.database.connectionTest !== null && (
                  <div>Connection: 
                    {config.bindings.database.connectionTest ? (
                      <span className="text-green-600 font-medium"> ✓ Connected</span>
                    ) : (
                      <span className="text-red-600 font-medium"> ✗ Failed</span>
                    )}
                  </div>
                )}
                {config.bindings.database.tables && config.bindings.database.tables.length > 0 && (
                  <div>
                    Tables ({config.bindings.database.tables.length}): 
                    <span className="font-mono text-xs"> {config.bindings.database.tables.join(', ')}</span>
                  </div>
                )}
                {config.bindings.database.error && (
                  <div className="text-red-600">Error: {config.bindings.database.error}</div>
                )}
              </div>
            </div>

            {/* Services */}
            {Object.entries(config.bindings.services).map(([name, service]) => (
              <div key={name} className="p-3 bg-gray-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Server className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium capitalize">{name} Service</p>
                      <p className="text-sm text-gray-600">Expected: {service.expectedService}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {service.configured ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
                
                {service.status && (
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Status: 
                      {service.status.connected ? (
                        <span className="text-green-600 font-medium"> ✓ Connected</span>
                      ) : (
                        <span className="text-red-600 font-medium"> ✗ Failed</span>
                      )}
                    </div>
                    
                    {service.status.environment && (
                      <div>Environment: <span className="font-medium">{service.status.environment}</span></div>
                    )}
                    
                    {service.status.version && (
                      <div>Version: <span className="font-mono text-xs">{service.status.version}</span></div>
                    )}
                    
                    {service.status.uptime && (
                      <div>Uptime: <span className="font-medium">{service.status.uptime}</span></div>
                    )}
                    
                    {service.status.userCount !== null && service.status.userCount !== undefined && (
                      <div>Users: <span className="font-medium">{service.status.userCount}</span></div>
                    )}
                    
                    {service.status.activeUsers !== null && service.status.activeUsers !== undefined && (
                      <div>Active Users: <span className="font-medium">{service.status.activeUsers}</span></div>
                    )}
                    
                    {service.status.hasSecrets !== undefined && (
                      <div>Secrets: 
                        {service.status.hasSecrets ? (
                          <span className="text-green-600 font-medium"> ✓</span>
                        ) : (
                          <span className="text-red-600 font-medium"> Missing</span>
                        )}
                      </div>
                    )}
                    
                    {service.status.secretsCheck && service.status.secretsCheck.details.length > 0 && (
                      <div className="mt-2">
                        <details className="cursor-pointer">
                          <summary className="text-xs text-gray-500 hover:text-gray-700">
                            Secret Details ({service.status.secretsCheck.details.length})
                          </summary>
                          <div className="mt-1 space-y-1">
                            {service.status.secretsCheck.details.map((detail: string, idx: number) => (
                              <div key={idx} className="text-xs font-mono">
                                {detail}
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                    
                    {service.status.endpoints && Object.keys(service.status.endpoints).length > 0 && (
                      <div className="mt-2">
                        <details className="cursor-pointer">
                          <summary className="text-xs text-gray-500 hover:text-gray-700">
                            Available Endpoints ({Object.keys(service.status.endpoints).length})
                          </summary>
                          <div className="mt-1 space-y-1">
                            {Object.entries(service.status.endpoints).map(([endpoint, info]: [string, any]) => (
                              <div key={endpoint} className="text-xs">
                                <span className="font-mono">{endpoint}</span>: 
                                {info.available ? (
                                  <span className="text-green-600"> ✓</span>
                                ) : (
                                  <span className="text-red-600"> ✗ ({info.status || 'Failed'})</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                    
                    {service.status.error && (
                      <div className="text-red-600">Error: {service.status.error}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Environment Variables */}
      {config.environment.variables && Object.keys(config.environment.variables).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Environment Variables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(config.environment.variables).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm text-gray-600">{key}</p>
                  <p className="font-mono text-sm">{String(value)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};